import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type { ModyoCollectionResponse, ModyoFilterBuilder } from '@modyo/sdk';
import { from, map, type Observable } from 'rxjs';
import { ServicioClienteModyo } from './modyo-cliente.service';
import type { ModyoContentOptions } from './modyo.types';
import type { ModyoContentQueryOptions } from './modyo-contenido.types';
import type {
  ErrorCMSContent,
  ErrorMappingResponse,
  ErrorAnalysisPageConfig,
  ErrorCategory,
  ErrorSeverity,
  ErrorMappingRawFields,
  ModyoFieldValue,
} from '@core/interfaces/error-analysis.interface';
import type { ModyoEntryResponse } from '@core/interfaces/mensaje-error.type';

@Injectable({ providedIn: 'root' })
export class ServicioContenidoModyo {
  private readonly servicioCliente = inject(ServicioClienteModyo);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly esNavegador = isPlatformBrowser(this.platformId);

  public crearFiltro(uidTipo: string, opciones?: ModyoContentOptions): ModyoFilterBuilder {
    return this.servicioCliente.obtenerTipoContenido(uidTipo, opciones).Filter();
  }

  public obtenerEntradas<TEntrada = unknown>(
    uidTipo: string,
    opciones: ModyoContentQueryOptions = {},
  ): Observable<ModyoCollectionResponse<TEntrada>> {
    const { filter: filtro, ...opcionesContenido } = opciones;
    const tipoContenido = this.servicioCliente.obtenerTipoContenido<TEntrada>(uidTipo, opcionesContenido);

    return from(tipoContenido.getEntries<TEntrada>(filtro));
  }

  public obtenerEntradaPorSlug<TEntrada = unknown>(
    uidTipo: string,
    slug: string,
    opciones?: ModyoContentOptions,
  ): Observable<TEntrada> {
    const tipoContenido = this.servicioCliente.obtenerTipoContenido<TEntrada>(uidTipo, opciones);

    return from(tipoContenido.getEntryBySlug<TEntrada>(slug));
  }

  public obtenerEntradaPorUuid<TEntrada = unknown>(
    uidTipo: string,
    uuid: string,
    opciones?: ModyoContentOptions,
  ): Observable<TEntrada> {
    const tipoContenido = this.servicioCliente.obtenerTipoContenido<TEntrada>(uidTipo, opciones);

    return from(tipoContenido.getEntry<TEntrada>(uuid));
  }

  public esContextoNavegador(): boolean {
    return this.esNavegador;
  }

  public obtenerMapeosErrores(): Observable<ErrorMappingResponse> {
    return this.obtenerEntradas<ModyoEntryResponse<ErrorMappingRawFields>>('error_mapping').pipe(
      map((response) => ({
        errors: (response.entries || []).map((entry) => this.normalizarMapeoError(entry)),
      })),
    );
  }

  public obtenerConfiguracionPagina(pageId: string = 'error-analysis'): Observable<ErrorAnalysisPageConfig> {
    return this.obtenerEntradaPorSlug<ErrorAnalysisPageConfig>('error_analysis_page_config', pageId);
  }

  private normalizarMapeoError(entry: ModyoEntryResponse<ErrorMappingRawFields>): ErrorCMSContent {
    const fields = entry?.fields ?? {};

    const errorCode = this.extraerCampo(fields, ['errorCode', 'error_code', 'codigo']);
    const errorMessage = this.extraerCampo(fields, ['errorMessage', 'error_message']);
    const userFriendlyMessage = this.extraerCampo(fields, [
      'userFriendlyMessage',
      'user_friendly_message',
      'mensaje_usuario',
    ]);
    const errorCategory = this.extraerCampo(fields, ['errorCategory', 'error_category', 'categoria_error']);
    const severity = this.extraerCampo(fields, ['severity', 'severidad']);
    const additionalInfo = this.extraerCampo(fields, ['additionalInfo', 'additional_info']);

    return {
      errorCode,
      errorMessage,
      userFriendlyMessage,
      errorCategory: this.normalizarCategoria(errorCategory),
      severity: this.normalizarSeveridad(severity),
      additionalInfo: additionalInfo || undefined,
    };
  }

  private extraerCampo(campos: ErrorMappingRawFields, claves: Array<keyof ErrorMappingRawFields>): string {
    for (const clave of claves) {
      const valor = campos[clave];
      const normalizado = this.normalizarValor(valor);
      if (normalizado) {
        return normalizado;
      }
    }
    return '';
  }

  private normalizarValor(valor: ModyoFieldValue): string {
    if (valor === null || valor === undefined) {
      return '';
    }

    if (typeof valor === 'string') {
      return valor.trim();
    }

    if (typeof valor === 'number' || typeof valor === 'boolean') {
      return String(valor);
    }

    if (Array.isArray(valor)) {
      return valor
        .map((item) => this.normalizarValor(item as ModyoFieldValue))
        .filter(Boolean)
        .join(', ');
    }

    if (typeof valor === 'object') {
      if ('value' in valor) {
        return this.normalizarValor((valor as { value?: ModyoFieldValue }).value);
      }

      try {
        return JSON.stringify(valor);
      } catch {
        return '';
      }
    }

    return '';
  }

  private normalizarCategoria(valor: string): ErrorCategory {
    const normalizado = valor
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

    switch (normalizado) {
      case 'tecnico':
        return 'técnico';
      case 'negocio':
        return 'negocio';
      case 'validacion':
        return 'validación';
      case 'integracion':
        return 'integración';
      default:
        return 'técnico';
    }
  }

  private normalizarSeveridad(valor: string): ErrorSeverity {
    const normalizado = valor.trim().toLowerCase();

    switch (normalizado) {
      case 'critical':
        return 'critical';
      case 'high':
        return 'high';
      case 'low':
        return 'low';
      default:
        return 'medium';
    }
  }
}
